<template>
  <!--
    Standalone preview of a monochrome topographic-contour hero.
    Pure client-side (canvas + d3.contours); no backend, no API key.
    Lines follow the active light/dark theme (body.dark-mode + 'theme-change').
  -->
  <!--
    Background mode teleports to <body>: the router-view's page-transition applies
    a transform, which would otherwise make this fixed layer size to that
    transformed ancestor (a box that flashes then expands). At body level it is
    relative to the viewport from the first frame.
  -->
  <teleport to="body" :disabled="!background">
    <div class="contour-hero" :class="{ 'as-background': background, 'as-band': band }" ref="root">
      <canvas ref="canvas" class="contour-canvas" aria-hidden="true"></canvas>
      <template v-if="!background">
        <div class="contour-overlay">
          <i class="far fa-compass contour-compass" aria-hidden="true"></i>
          <h1 class="contour-title">{{ title }}</h1>
          <p class="contour-sub" v-if="subtitle">{{ subtitle }}</p>
        </div>
        <p class="contour-note" v-if="note">
          Prototype: a living topographic field, drawn with the same d3 already on the site.
          Drifts on its own; the cursor raises a gentle peak.
        </p>
      </template>
    </div>
  </teleport>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'ContourHero',
  props: {
    // Background mode: no overlay text, fills the first viewport behind page
    // content (pointer-events:none, z-index:-1), tracks the cursor via window,
    // and renders the lines fainter so it reads as ambient texture.
    background: { type: Boolean, default: false },
    // Band mode: a shorter, contained hero strip (subject, not backdrop).
    band: { type: Boolean, default: false },
    title: { type: String, default: 'Explore. Understand. Build.' },
    subtitle: { type: String, default: 'Move your cursor over the terrain.' },
    note: { type: Boolean, default: true },
    // Background mode: CSS selectors whose live bounding boxes stay cleared of
    // lines (the cleared region follows them as the page scrolls).
    maskSelectors: { type: Array, default: () => [] },
  },
  data() {
    return {
      ctx: null,
      raf: null,
      gw: 0,
      gh: 0,
      values: null,
      contour: null,
      W: 0,
      H: 0,
      dpr: 1,
      mouse: { x: -1, y: -1, active: 0 }, // grid coords + presence ramp [0,1]
      dark: false,
      reduced: false,
      lastT: 0,
      t0: 0,
    };
  },
  mounted() {
    this._suspended = false;
    if (this.background) {
      document.body.classList.add('has-contour-background');
    }
    this.reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.dark = document.body.classList.contains('dark-mode');
    this._onTheme = (e) => { this.dark = (e.detail && e.detail.theme === 'dark')
      || document.body.classList.contains('dark-mode'); };
    window.addEventListener('theme-change', this._onTheme);

    this._onResize = () => this.layout();
    window.addEventListener('resize', this._onResize, { passive: true });

    const root = this.$refs.root;
    // window 'resize' misses layout shifts that change the element's own box
    // (e.g. a scrollbar appearing once content reveals, which narrows a fixed
    // backdrop and left a blank strip on first paint). Observe the box directly.
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.layout());
      this._ro.observe(root);
    }
    this._onMove = (ev) => {
      const r = root.getBoundingClientRect();
      const px = (ev.clientX - r.left) / r.width;
      const py = (ev.clientY - r.top) / r.height;
      // Outside the field (e.g. scrolled past it in background mode): drop the peak.
      if (px < 0 || px > 1 || py < 0 || py > 1) { this.mouse.x = -1; this.mouse.y = -1; return; }
      this.mouse.x = px * this.gw;
      this.mouse.y = py * this.gh;
    };
    this._onLeave = () => { this.mouse.x = -1; this.mouse.y = -1; };
    // Behind page content the canvas can't receive events, so track on window.
    this._moveTarget = this.background ? window : root;
    this._moveTarget.addEventListener('mousemove', this._onMove, { passive: true });
    root.addEventListener('mouseleave', this._onLeave, { passive: true });

    this.ctx = this.$refs.canvas.getContext('2d');
    this.contour = d3.contours();
    this.layout();
    this.t0 = performance.now();
    if (this.reduced) {
      this.frame(this.t0, true); // single static frame
    } else {
      this.raf = requestAnimationFrame(this.frame);
    }
  },
  activated() {
    this.resumeContour();
  },
  deactivated() {
    this.suspendContour();
  },
  beforeUnmount() {
    this.suspendContour();
    if (this._ro) this._ro.disconnect();
    window.removeEventListener('theme-change', this._onTheme);
    window.removeEventListener('resize', this._onResize);
    const root = this.$refs.root;
    if (this._moveTarget) this._moveTarget.removeEventListener('mousemove', this._onMove);
    if (root) root.removeEventListener('mouseleave', this._onLeave);
  },
  methods: {
    suspendContour() {
      this._suspended = true;
      this._pausedAt = performance.now();
      if (this.background) document.body.classList.remove('has-contour-background');
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      const root = this.$refs.root;
      if (root) root.hidden = true;
    },
    resumeContour() {
      if (!this.ctx || !this.contour) return;
      const now = performance.now();
      if (this._pausedAt) this.t0 += now - this._pausedAt;
      this._pausedAt = 0;
      this._suspended = false;
      const root = this.$refs.root;
      if (root) root.hidden = false;
      if (this.background) document.body.classList.add('has-contour-background');
      this.$nextTick(() => {
        this.layout();
        if (this.reduced) {
          this.frame(performance.now(), true);
        } else if (!this.raf) {
          this.raf = requestAnimationFrame(this.frame);
        }
      });
    },
    layout() {
      const root = this.$refs.root;
      const cv = this.$refs.canvas;
      if (!root || !cv || !this.contour || this._suspended) return;
      const rect = root.getBoundingClientRect();
      this.W = Math.max(1, Math.round(rect.width));
      this.H = Math.max(1, Math.round(rect.height));
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);

      cv.width = this.W * this.dpr;
      cv.height = this.H * this.dpr;
      cv.style.width = this.W + 'px';
      cv.style.height = this.H + 'px';

      // Field resolution: independent of pixel size, tuned for ~30fps.
      this.gw = Math.max(56, Math.min(132, Math.round(this.W / 9)));
      this.gh = Math.max(36, Math.round(this.gw * (this.H / this.W)));
      this.values = new Float64Array(this.gw * this.gh);
      this.contour.size([this.gw, this.gh]);

      if (this.reduced) this.frame(performance.now(), true);
    },

    // Cheap layered-sinusoid value noise, normalised to [0,1].
    field(t) {
      const { gw, gh, values, mouse } = this;
      // Ramp cursor presence in/out for a soft response.
      mouse.active += ((mouse.x >= 0 ? 1 : 0) - mouse.active) * 0.08;
      const sig = gw * 0.16;          // bump radius
      const inv2s2 = 1 / (2 * sig * sig);
      let i = 0;
      for (let y = 0; y < gh; y++) {
        for (let x = 0; x < gw; x++) {
          let v =
            0.55 * Math.sin(x * 0.135 + t * 0.21) * Math.cos(y * 0.115 - t * 0.16) +
            0.30 * Math.sin((x + y) * 0.085 - t * 0.12) +
            0.22 * Math.cos(x * 0.045 - y * 0.07 + t * 0.08) +
            0.18 * Math.sin(y * 0.16 + t * 0.05);
          let f = 0.5 + 0.5 * (v / 1.25);
          if (mouse.active > 0.001 && mouse.x >= 0) {
            const dx = x - mouse.x, dy = y - mouse.y;
            f += 0.6 * mouse.active * Math.exp(-(dx * dx + dy * dy) * inv2s2);
          }
          values[i++] = f;
        }
      }
    },

    frame(now, once) {
      if (this._suspended) return;
      if (!once && !this.reduced) this.raf = requestAnimationFrame(this.frame);
      // Cap to ~33ms to keep contour recompute affordable.
      if (!once && now - this.lastT < 32) return;
      this.lastT = now;

      const t = (now - this.t0) / 1000;
      this.field(this.reduced ? 0 : t);

      const { ctx, W, H, gw, gh, dpr } = this;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      const thresholds = d3.range(0.18, 1.18, 0.072);
      const polys = this.contour.thresholds(thresholds)(this.values);

      const sx = W / gw, sy = H / gh;
      const proj = d3.geoTransform({
        point(px, py) { this.stream.point(px * sx, py * sy); },
      });
      const path = d3.geoPath(proj, ctx);

      const base = this.dark ? 216 : 60;   // grey level of the lines
      const fade = this.background ? 0.55 : 1; // ambient backdrop reads fainter
      const n = polys.length;
      for (let k = 0; k < n; k++) {
        const depth = k / Math.max(1, n - 1);       // 0 low ground .. 1 peaks
        const alpha = (0.10 + 0.42 * depth) * fade; // higher ground reads stronger
        ctx.beginPath();
        path(polys[k]);
        ctx.lineWidth = 0.9 + 0.7 * depth;
        ctx.strokeStyle = `rgba(${base},${base},${base},${alpha})`;
        ctx.stroke();
      }

      // Dynamic mask: carve a soft hole over the live position of the content
      // (text + globe) so the cleared zone tracks the page as it scrolls.
      this.carveMask(ctx);
    },

    carveMask(ctx) {
      if (!this.maskSelectors.length) return;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      for (const sel of this.maskSelectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue;
        // A soft ellipse over each element; the feather bridges adjacent holes
        // so the title, intro and globe read as one continuous cleared zone.
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const rx = (r.width / 2) * 1.12 + 46;
        const ry = (r.height / 2) * 1.12 + 46;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(rx, ry);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
        g.addColorStop(0, 'rgba(0,0,0,1)');   // fully clear over the content
        g.addColorStop(0.68, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');    // feather back to lines outside
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, 1, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();
    },
  },
};
</script>

<style scoped>
.contour-hero {
  position: relative;
  width: 100%;
  height: 78vh;
  min-height: 460px;
  overflow: hidden;
}
.contour-canvas {
  position: absolute;
  inset: 0;
  display: block;
}
/* Ambient backdrop: pinned to the first viewport, behind all page content. */
.contour-hero.as-background {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100vh;
  min-height: 0;
  z-index: 0;
  pointer-events: none;
}
:global(body.has-contour-background #app) {
  background-color: transparent;
}

:global(body.has-contour-background .container) {
  position: relative;
  z-index: 1;
}
/* The cleared region is carved per-frame in canvas (carveMask) so it can follow
   the content as the page scrolls; see maskSelectors. */
/* Contained hero strip: it is the subject, framed, with breathing room. */
.contour-hero.as-band {
  height: 46vh;
  min-height: 300px;
  border: 1px solid var(--line, #e3e3ea);
  border-radius: 4px;
  margin: 0.5em 0 1.6em;
}
body.dark-mode .contour-hero.as-band { border-color: #2a2a2a; }
.contour-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
}
.contour-compass {
  font-size: 2.4em;
  color: #767676;
  opacity: 0.85;
  margin-bottom: 0.35em;
}
.contour-title {
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.04em;
  color: #333;
}
.contour-sub {
  margin: 0.4em 0 0;
  font-size: 0.95em;
  color: #999;
}
.contour-note {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 1.1em;
  margin: 0 auto;
  max-width: 36em;
  padding: 0 1.2em;
  text-align: center;
  font-size: 0.82em;
  line-height: 1.5;
  color: #9a9a9a;
}
body.dark-mode .contour-title { color: #ccc; }
body.dark-mode .contour-sub,
body.dark-mode .contour-note { color: var(--site-text-tertiary); }
body.dark-mode .contour-compass { color: var(--site-text-tertiary); }
</style>
